// ─── STYLES ──────────────────────────────────────────────────────────────────
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Instrument+Serif:ital@0;1&family=Fredoka:wght@600&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    :root{
      /* ── Bloom design system v0.1 (light Market) — see design-system/tokens.json ── */
      --ink:#0B0B0B;--ink-soft:#6B675C;--text2:#3F3C35;
      --white:#FFFFFF;--offwhite:#F4F3EE;--surface:#ECE9DD;
      /* ── Accent: cobalt blue; butter yellow for highlights (see --butter). One accent, no gradients. ──────── */
      --teal:#2F63DD;--teal-deep:#214EB8;--teal-light:#E6EDFC;--teal-mid:#B9CCF5;
      /* ── Sale / destructive red ──────────────────── */
      --red:#B42318;--red-light:#FEF3F2;
      /* ── Alias tokens (keep old names working) ───── */
      --pink:#E6EDFC;--pink-deep:#D3E0FA;
      --pink-btn:#2F63DD;--pink-btn-h:#214EB8;
      --yellow:#FFFAEB;--yellow-deep:#FEDF89;
      --blue:#EFF8FF;--blue-deep:#B2DDFF;
      /* ── Structural ───────────────────────────────── */
      --border:#E2DFD3;--border-mid:#CFCBBE;
      --bg:#FFFFFF;--shadow-sm:0 1px 2px rgba(16,24,40,0.06);--shadow:0 4px 12px rgba(16,24,40,0.08);
      --green:#067647;--green-bg:#ECFDF3;--r:4px;
      /* ── Semantic tokens: every page uses these, so the Black / Neutral store themes can restyle them ── */
      --head-bg:#F7F6F1;--row-line:#EFECE4;--sel-bg:#EEF3FD;
      --warn-bg:#FFFAEB;--warn-bd:#FEC84B;--warn-fg:#B54708;
      --info-bg:#EFF8FF;--info-bd:#B2DDFF;--info-fg:#175CD3;
      --danger-bd:#FDA29B;--ok-bd:#ABEFC6;
      --on-accent:#FFFFFF;--on-ink:#FFFFFF;--overlay:rgba(16,24,40,0.55);--star:#F59E0B;
      /* ── Flat tokens (old gradient names kept so nothing breaks) ── */
      --grad-hero:#F7F6F1;--grad-promo:#F8F5A0;--sun:#FFC933;
      --butter:#F8F5A0;--butter-deep:#EFEA7A;
      --grad-accent:#2F63DD;
      --grad-warm:#F7F6F1;
    }
    @font-face{font-family:"RupeeFix";src:local("Noto Sans"),local("Segoe UI"),local("Roboto"),local("Arial");unicode-range:U+20B9;}
    body{background:var(--offwhite);font-family:"RupeeFix",'Syne',sans-serif;color:var(--ink);scroll-behavior:smooth;overflow-x:hidden;font-variant-numeric:tabular-nums;-webkit-font-smoothing:antialiased;}
    #root{overflow-x:hidden;overflow-x:clip;}
    button,input,select,textarea{font-family:inherit;}
    h1,h2,h3{font-family:"RupeeFix",'Syne',sans-serif;font-weight:700;line-height:1.15;}
    h1 em,h2 em,h3 em,h4 em{font-style:normal;}
    h4{font-family:"RupeeFix",'Syne',sans-serif;font-weight:700;}
    ::-webkit-scrollbar{width:5px;}
    ::-webkit-scrollbar-track{background:var(--teal-light);}
    ::-webkit-scrollbar-thumb{background:var(--teal-mid);border-radius:2px;}

    /* ── Display type: tall condensed serif (headlines) and the rounded wordmark face ── */
    .serif{font-family:'Instrument Serif',Georgia,serif;font-weight:400;letter-spacing:-0.02em;line-height:.92;text-transform:uppercase;}
    .serif-sentence{font-family:'Instrument Serif',Georgia,serif;font-weight:400;letter-spacing:-0.015em;line-height:1;}
    .brandface{font-family:'Fredoka','Nunito',system-ui,sans-serif;font-weight:600;letter-spacing:-0.01em;}
    .nav-blue input::placeholder{color:rgba(255,255,255,0.88);}

    /* ── Keyframes ─────────────────────────────────── */
    @keyframes spin{to{transform:rotate(360deg);}}
    @keyframes fadeUp{from{opacity:0;transform:translateY(22px);}to{opacity:1;transform:translateY(0);}}
    @keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
    @keyframes slideInRight{from{opacity:0;transform:translateX(28px);}to{opacity:1;transform:translateX(0);}}
    @keyframes slideInLeft{from{opacity:0;transform:translateX(-28px);}to{opacity:1;transform:translateX(0);}}
    @keyframes floatOrb{0%,100%{transform:translateY(0) scale(1);}50%{transform:translateY(-18px) scale(1.05);}}
    @keyframes shimPro{0%{background-position:-300% 0;}100%{background-position:300% 0;}}
    @keyframes shimmer{0%{background-position:-200% 0;}100%{background-position:200% 0;}}
    @keyframes popIn{0%{opacity:0;transform:scale(0.86);}70%{transform:scale(1.02);}100%{opacity:1;transform:scale(1);}}
    @keyframes checkDraw{0%{stroke-dashoffset:60;}100%{stroke-dashoffset:0;}}
    @keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(6,118,71,0.4);}50%{box-shadow:0 0 0 10px rgba(6,118,71,0);}}
    @keyframes tealPulse{0%,100%{box-shadow:0 0 0 0 rgba(47,99,221,0.45);}50%{box-shadow:0 0 0 12px rgba(47,99,221,0);}}
    @keyframes liveDot{0%,100%{opacity:1;}50%{opacity:0.3;}}
    @keyframes gradientShift{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
    @keyframes glowRing{0%,100%{box-shadow:0 0 0 0 rgba(47,99,221,0.5);}50%{box-shadow:0 0 0 8px rgba(47,99,221,0);}}
    @keyframes soldPop{0%{opacity:0;transform:scale(0.7);}60%{transform:scale(1.1);}100%{opacity:1;transform:scale(1);}}
    @keyframes pinBounce{0%{transform:translateY(0);}30%{transform:translateY(-4px);}60%{transform:translateY(2px);}100%{transform:translateY(0);}}
    @keyframes atcSlideUp{from{transform:translateY(100%);opacity:0;}to{transform:translateY(0);opacity:1;}}

    /* ── Utility animation classes ──────────────────── */
    .fadeUp{animation:fadeUp 0.55s cubic-bezier(.22,.68,0,1.2) both;}
    .fadeIn{animation:fadeIn 0.4s ease both;}
    .pop-in{animation:popIn 0.38s cubic-bezier(.22,.68,0,1.2) both;}
    .slide-right{animation:slideInRight 0.45s cubic-bezier(.22,.68,0,1.2) both;}

    /* ── Buttons ────────────────────────────────────── */
    .btn-pink{background:var(--teal);color:#fff;border:1px solid var(--teal);border-radius:4px;padding:12px 24px;font-family:'Syne',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:background 0.12s,border-color 0.12s;}
    .btn-pink:hover{background:var(--teal-deep);border-color:var(--teal-deep);}
    .btn-pink:active{background:var(--teal-deep);}
    .btn-pink:disabled{opacity:0.5;cursor:not-allowed;transform:none;box-shadow:none;}
    .btn-yellow{background:var(--white);color:var(--ink);border:1px solid var(--ink);border-radius:4px;padding:12px 24px;font-family:'Syne',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:background 0.12s;}
    .btn-yellow:hover{background:var(--surface);}
    .btn-green{background:var(--teal);color:#fff;border:1px solid var(--teal);border-radius:4px;padding:12px 24px;font-family:'Syne',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:background 0.12s;}
    .btn-green:hover{background:var(--teal-deep);border-color:var(--teal-deep);}
    .btn-ghost{background:var(--white);color:var(--ink);border:1px solid var(--border-mid);border-radius:4px;padding:11px 22px;font-family:'Syne',sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:border-color 0.12s,background 0.12s;}
    .btn-ghost:hover{border-color:var(--ink);background:var(--surface);}

    /* ── Inputs ─────────────────────────────────────── */
    .inp{width:100%;background:var(--white);border:1px solid #948C80;border-radius:4px;padding:11px 14px;font-family:'Syne',sans-serif;font-size:15px;color:var(--ink);outline:none;transition:border-color 0.12s,box-shadow 0.12s;}
    .inp:focus{border-color:var(--teal);box-shadow:0 0 0 2px rgba(47,99,221,0.2);}
    .inp::placeholder{color:#948C80;}
    .inp.err{border-color:var(--red);}

    /* ── Cards ──────────────────────────────────────── */
    .card{background:var(--white);border:1px solid var(--border);border-radius:var(--r);box-shadow:none;transition:box-shadow 0.12s;}

    /* ── Pills & badges ─────────────────────────────── */
    .pill{display:inline-flex;align-items:center;gap:5px;background:var(--teal-light);border-radius:2px;padding:3px 10px;font-size:12px;font-weight:600;letter-spacing:0;text-transform:none;color:var(--teal-deep);}
    .bdg-green{background:#ECFDF3;color:#067647;border-radius:2px;padding:3px 9px;font-size:12px;font-weight:600;}
    .bdg-yellow{background:#FEF3F2;color:#B42318;border-radius:2px;padding:3px 9px;font-size:12px;font-weight:600;}

    /* ── Pro shimmer ────────────────────────────────── */
    .pro-shim{color:var(--teal-deep);font-weight:700;}

    /* ── UPI buttons ────────────────────────────────── */
    .upi-btn{display:flex;flex-direction:column;align-items:center;gap:6px;background:var(--surface);border:1px solid var(--border-mid);border-radius:4px;padding:12px 10px;cursor:pointer;transition:border-color 0.12s,background 0.12s;font-size:12px;font-weight:600;color:var(--ink-soft);}
    .upi-btn:hover{border-color:var(--teal);background:var(--teal-light);color:var(--teal-deep);}

    /* ── Upload zone ────────────────────────────────── */
    .upload-zone{border:1px dashed #948C80;border-radius:4px;padding:28px;text-align:center;cursor:pointer;transition:border-color 0.12s,background 0.12s;}
    .upload-zone:hover,.upload-zone.drag{border-color:var(--teal);background:var(--teal-light);}

    /* ── Sold-out badge ─────────────────────────────── */
    .sold-badge{animation:soldPop 0.4s cubic-bezier(.22,.68,0,1.2) both;}

    /* ── Pinned badge ───────────────────────────────── */
    .pin-badge{animation:pinBounce 0.5s ease both;}

    /* ── Noise overlay ──────────────────────────────── */
    .noise-bg::before{content:'';position:fixed;inset:0;pointer-events:none;z-index:9999;opacity:0.3;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.025'/%3E%3C/svg%3E");}

    /* ── Product card hover ─────────────────────────── */
    .prod-card:hover{transform:none !important;box-shadow:0 4px 12px rgba(16,24,40,0.08) !important;}

    /* ── Smooth gradient animated hero ──────────────── */
    .hero-grad{background:#F8F6F3;}

    /* ── Teal glow button ───────────────────────────── */
    .btn-glow{}

    /* ── Dark nav overrides ─────────────────────────── */
    .nav-dark .btn-ghost{color:rgba(255,255,255,0.80);border-color:rgba(255,255,255,0.15);}
    .nav-dark .btn-ghost:hover{background:rgba(255,255,255,0.09);border-color:rgba(255,255,255,0.32);color:#fff;}
  `}</style>
);

export default FontLoader;
